import React, { Children, ReactNode, RefObject, cloneElement } from 'react';
import { InView as ReactIntersectionObserver } from 'react-intersection-observer';
import { useInViewDelay } from './useInViewDelay';

type ScrollAnimatorProps<E extends HTMLElement> = {
  delay?: number;
  isInView?: boolean;
  threshold?: number;
  children?: RenderCallback<E>;
  render?: RenderCallback<E>;
};

type RenderCallback<E extends HTMLElement> = (args: ScrollAnimatorComponentProps<E>) => ReactNode;

type ScrollAnimatorComponentProps<E extends HTMLElement> = {
  isInView: ScrollAnimatorProps<E>['isInView'];
  ref: RefObject<E>;
};

/**
 * Usage:
 *     <ScrollAnimator>
 *       {({ isInView }) => (
 *         <GridCell
 *           entry={item}
 *           className={cx(styles.gridCell, { 'in-view': isInView })}
 *         />
 *       )}
 *     </ScrollAnimator>
 */
export const ScrollAnimator = <E extends HTMLElement>(props: ScrollAnimatorProps<E>) => {
  const { delay, threshold = 0.3, isInView: initialIsInView, children, render } = props;
  const { isInView, onChange } = useInViewDelay({ delay, initialIsInView });

  const child = render || children;
  if (!child) return null;

  return (
    <ReactIntersectionObserver triggerOnce threshold={threshold} onChange={onChange}>
      {({ ref }) =>
        typeof child === 'function'
          ? child({ isInView, ref: ref as RefObject<E> })
          : cloneElement(Children.only(child), { ref })
      }
    </ReactIntersectionObserver>
  );
};
